import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  FormHelperText,
  InputAdornment,
  Autocomplete,
  Alert,
  Snackbar
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Close as CloseIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Cigar, useGetCigarLinesQuery, useCreateCigarMutation, useUpdateCigarMutation, useGetBrandsQuery } from '@store/api/cigarApi'
import { useGetHumidorsQuery } from '@store/api/humidorApi'
import { useGetVitolasQuery } from '@store/api/vitolaApi'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { offlineCreateCigar, offlineUpdateCigar } from '@services/offlineWriteService'
import { resolveVitolaFromLineName } from '@utils/resolveVitolaFromLine'

interface CigarFormProps {
  open: boolean
  onClose: () => void
  cigar?: Cigar | null
  onSaved?: () => void
}

const statusOptions = [
  { value: 'available', label: 'Available' },
  { value: 'smoked', label: 'Smoked' },
  { value: 'traded', label: 'Traded' },
  { value: 'gifted', label: 'Gifted' }
]

// Vitola options will be fetched from API

const cigarFormSchema = (isEdit: boolean) => yup.object({
  brandId: yup.string().required('Brand is required'),
  cigarLineId: yup.string().required('Cigar line is required'),
  humidorId: yup.string().required('Humidor is required'),
  vitolaId: yup.string().required('Vitola is required'),
  quantity: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ||
      originalValue === null ||
      (typeof originalValue === 'number' && Number.isNaN(originalValue)) ||
      Number.isNaN(value)
        ? undefined
        : value
    )
    .min(isEdit ? 0 : 1, isEdit ? 'Quantity cannot be negative' : 'Quantity must be at least 1')
    .max(1000, 'Quantity cannot exceed 1000')
    .required('Quantity is required'),
  length: yup.number().min(0, 'Length must be non-negative').max(12, 'Length cannot exceed 12 inches'),
  ringGauge: yup.number().min(0, 'Ring gauge must be non-negative').max(80, 'Ring gauge cannot exceed 80'),
  purchasePrice: yup.number().min(0, 'Price must be non-negative').max(10000, 'Price cannot exceed $10,000'),
  purchaseDate: yup.date(),
  purchaseLocation: yup.string().max(255, 'Location cannot exceed 255 characters'),
  notes: yup.string().max(500, 'Notes cannot exceed 500 characters'),
  status: yup.string().oneOf(['available', 'smoked', 'traded', 'gifted']),
  batchNumber: yup.string().max(50, 'Batch number cannot exceed 50 characters'),
  wrapperType: yup.string().max(100, 'Wrapper type cannot exceed 100 characters'),
  binderType: yup.string().max(100, 'Binder type cannot exceed 100 characters'),
  fillerType: yup.string().max(100, 'Filler type cannot exceed 100 characters'),
  strengthLevel: yup.string().max(50, 'Strength level cannot exceed 50 characters')
})

type FormData = yup.InferType<ReturnType<typeof cigarFormSchema>>

const CigarForm: React.FC<CigarFormProps> = ({
  open,
  onClose,
  cigar,
  onSaved,
}) => {
  const dispatch = useAppDispatch()
  const { isOnline } = useAppSelector((state) => state.offline)
  const isEdit = Boolean(cigar)
  const schema = useMemo(() => cigarFormSchema(isEdit), [isEdit])
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      brandId: '',
      cigarLineId: '',
      humidorId: '',
      vitolaId: '',
      quantity: 1,
      length: 0,
      ringGauge: 0,
      purchasePrice: 0,
      purchaseDate: undefined,
      purchaseLocation: '',
      notes: '',
      status: 'available',
      batchNumber: '',
      wrapperType: '',
      binderType: '',
      fillerType: '',
      strengthLevel: ''
    }
  })

  const watchedBrandId = watch('brandId')
  const watchedCigarLineId = watch('cigarLineId')
  const watchedVitolaId = watch('vitolaId')
  const watchedHumidorId = watch('humidorId')

  const { data: cigarLinesData } = useGetCigarLinesQuery({ 
    search: '', 
    brandId: watchedBrandId || undefined 
  }, {
    skip: !watchedBrandId // Skip the query if no brand is selected
  })
  const { data: brandsData } = useGetBrandsQuery({ search: '' })
  const { data: humidorsData } = useGetHumidorsQuery()
  const { data: vitolasData } = useGetVitolasQuery(
    { active: true, cigarLineId: watchedCigarLineId },
    { skip: !watchedCigarLineId }
  )
  const [createCigar, { isLoading: createLoading }] = useCreateCigarMutation()
  const [updateCigar, { isLoading: updateLoading }] = useUpdateCigarMutation()

  const loading = createLoading || updateLoading

  // Track which line we last applied catalog autofill for, and whether the
  // user manually overrode vitola (so refetches don't clobber their choice).
  const lastLineAutofillRef = useRef<string | null>(null)
  const userChoseVitolaRef = useRef(false)
  // Previous watched vitola — used to restore stored dims when reverting A→B→A.
  const prevWatchedVitolaRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    prevWatchedVitolaRef.current = undefined
    if (cigar) {
      lastLineAutofillRef.current = cigar.cigarLineId
      userChoseVitolaRef.current = true
    } else {
      lastLineAutofillRef.current = null
      userChoseVitolaRef.current = false
    }
  }, [cigar])

  // Reset dependent fields when brand changes (create flow only)
  useEffect(() => {
    if (cigar || !watchedBrandId) return
    setValue('cigarLineId', '')
    setValue('vitolaId', '')
    setValue('length', 0)
    setValue('ringGauge', 0)
    lastLineAutofillRef.current = null
    userChoseVitolaRef.current = false
  }, [watchedBrandId, setValue, cigar])

  // Clear cigarLineId if it's not in the available options
  useEffect(() => {
    if (watchedCigarLineId && Array.isArray(cigarLinesData)) {
      const isValidCigarLine = cigarLinesData.some(line => line.id === watchedCigarLineId)
      if (!isValidCigarLine) {
        setValue('cigarLineId', '')
      }
    }
  }, [watchedCigarLineId, cigarLinesData, setValue])

  // Clear vitolaId if it's not in the line-scoped options
  useEffect(() => {
    if (!watchedVitolaId || !watchedCigarLineId) return
    if (!Array.isArray(vitolasData)) return
    const isValidVitola = vitolasData.some((v) => v.id === watchedVitolaId)
    if (!isValidVitola) {
      setValue('vitolaId', '')
      setValue('length', 0)
      setValue('ringGauge', 0)
      userChoseVitolaRef.current = false
    }
  }, [watchedVitolaId, watchedCigarLineId, vitolasData, setValue])

  const applyVitolaDimensions = (vitolaId: string) => {
    if (!vitolaId || !Array.isArray(vitolasData)) return
    const selected = vitolasData.find((v) => v.id === vitolaId)
    if (!selected) return
    const nextLength = selected.length != null ? Number(selected.length) : NaN
    const nextRing = selected.ringGauge != null ? Number(selected.ringGauge) : NaN
    // Always write both fields so switching to a dimensionless vitola clears stale values
    setValue(
      'length',
      !Number.isNaN(nextLength) && nextLength > 0 ? nextLength : 0,
      { shouldValidate: true }
    )
    setValue(
      'ringGauge',
      !Number.isNaN(nextRing) && nextRing > 0 ? nextRing : 0,
      { shouldValidate: true }
    )
  }

  const applySavedCigarDimensions = () => {
    if (!cigar) return
    setValue('length', Number(cigar.length) || 0, { shouldValidate: true })
    setValue('ringGauge', Number(cigar.ringGauge) || 0, { shouldValidate: true })
  }

  const syncDimensionsForVitola = (vitolaId: string) => {
    if (!vitolaId) return
    // Reverting to the saved vitola must restore stored dims, not keep the
    // intermediate vitola's size (A → B → A).
    if (cigar && vitolaId === cigar.vitolaId) {
      applySavedCigarDimensions()
      return
    }
    applyVitolaDimensions(vitolaId)
  }

  // Auto-populate length / ring gauge from the selected vitola.
  // Edit mode: on open, skip while still on the saved vitola so catalog
  // defaults never overwrite stored dimensions. If the user switches away
  // and back, restore the saved cigar's dimensions.
  useEffect(() => {
    if (!watchedVitolaId || !Array.isArray(vitolasData)) return
    const prev = prevWatchedVitolaRef.current
    prevWatchedVitolaRef.current = watchedVitolaId

    if (cigar && watchedVitolaId === cigar.vitolaId) {
      if (prev !== undefined && prev !== watchedVitolaId) {
        applySavedCigarDimensions()
      }
      return
    }
    applyVitolaDimensions(watchedVitolaId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when watched vitola / catalog changes
  }, [watchedVitolaId, vitolasData, setValue, cigar])

  // Auto-populate cigar details when cigar line is selected
  useEffect(() => {
    if (!watchedCigarLineId || !Array.isArray(cigarLinesData)) return
    const selectedCigarLine = cigarLinesData.find(line => line.id === watchedCigarLineId)
    if (!selectedCigarLine) return

    const lineJustChanged = lastLineAutofillRef.current !== watchedCigarLineId
    if (lineJustChanged) {
      if (selectedCigarLine.wrapperType) {
        setValue('wrapperType', selectedCigarLine.wrapperType, { shouldValidate: true })
      }
      if (selectedCigarLine.binderType) {
        setValue('binderType', selectedCigarLine.binderType, { shouldValidate: true })
      }
      if (selectedCigarLine.fillerType) {
        setValue('fillerType', selectedCigarLine.fillerType, { shouldValidate: true })
      }
      if (selectedCigarLine.strengthLevel) {
        setValue('strengthLevel', selectedCigarLine.strengthLevel, { shouldValidate: true })
      }

      lastLineAutofillRef.current = watchedCigarLineId
      userChoseVitolaRef.current = false
    }

    // Resolve vitola from line name (create, or edit after the user picks a new line).
    // Skip when the user manually chose a vitola, and wait until vitolas are loaded.
    if (userChoseVitolaRef.current) return
    if (!Array.isArray(vitolasData)) return

    const matchedVitola = resolveVitolaFromLineName(selectedCigarLine.name, vitolasData)
    if (matchedVitola) {
      setValue('vitolaId', matchedVitola.id, { shouldValidate: true })
    } else {
      setValue('vitolaId', '', { shouldValidate: true })
      setValue('length', 0, { shouldValidate: true })
      setValue('ringGauge', 0, { shouldValidate: true })
    }
  }, [watchedCigarLineId, cigarLinesData, vitolasData, setValue])

  useEffect(() => {
    if (cigar) {
      reset({
        brandId: cigar.brandId,
        cigarLineId: cigar.cigarLineId,
        humidorId: cigar.humidorId,
        vitolaId: cigar.vitolaId,
        quantity: cigar.quantity ?? 1,
        length: cigar.length || 0,
        ringGauge: cigar.ringGauge || 0,
        purchasePrice: typeof cigar.purchasePrice === 'string' ? parseFloat(cigar.purchasePrice) || 0 : cigar.purchasePrice || 0,
        purchaseDate: cigar.purchaseDate ? new Date(cigar.purchaseDate) : undefined,
        purchaseLocation: cigar.purchaseLocation || '',
        notes: cigar.notes || '',
        status: cigar.status as any,
        batchNumber: cigar.batchNumber || '',
        wrapperType: cigar.wrapperType || '',
        binderType: cigar.binderType || '',
        fillerType: cigar.fillerType || '',
        strengthLevel: cigar.strengthLevel || ''
      })
    } else {
      reset({
        brandId: '',
        cigarLineId: '',
        humidorId: '',
        vitolaId: '',
        quantity: 1,
        length: 0,
        ringGauge: 0,
        purchasePrice: 0,
        purchaseDate: undefined,
        purchaseLocation: '',
        notes: '',
        status: 'available',
        batchNumber: '',
        wrapperType: '',
        binderType: '',
        fillerType: '',
        strengthLevel: ''
      })
    }
  }, [cigar, reset])

  // After create-form reset (or when humidors load): select the sole humidor
  useEffect(() => {
    if (cigar || watchedHumidorId) return
    if (!Array.isArray(humidorsData) || humidorsData.length !== 1) return
    const sole = humidorsData[0]
    if (!sole?.id) return
    setValue('humidorId', sole.id, { shouldValidate: true })
  }, [humidorsData, setValue, cigar, watchedHumidorId])

  const handleFormSubmit = async (data: FormData) => {
    try {
      const selectedVitola = Array.isArray(vitolasData)
        ? vitolasData.find((v) => v.id === data.vitolaId)
        : undefined
      const vitolaLength =
        selectedVitola?.length != null ? Number(selectedVitola.length) : undefined
      const vitolaRing =
        selectedVitola?.ringGauge != null ? Number(selectedVitola.ringGauge) : undefined
      // When vitola changes, form length/ring can still hold the previous size until
      // the autofill effect runs. Prefer catalog dims in that stale window; otherwise
      // keep explicit form values (including intentional overrides).
      const vitolaChanged = !cigar || data.vitolaId !== cigar.vitolaId
      const resolveDim = (
        formVal: number | undefined,
        vitolaVal: number | undefined,
        previousVal: number | undefined
      ) => {
        const formOk = formVal != null && formVal > 0
        const vitolaOk = vitolaVal != null && !Number.isNaN(vitolaVal) && vitolaVal > 0
        if (vitolaChanged && vitolaOk && formOk && previousVal != null && formVal === previousVal) {
          return vitolaVal
        }
        if (formOk) return formVal
        if (vitolaOk) return vitolaVal
        return undefined
      }
      const resolvedLength = resolveDim(
        data.length,
        vitolaLength,
        cigar?.length != null ? Number(cigar.length) : undefined
      )
      const resolvedRingGauge = resolveDim(
        data.ringGauge,
        vitolaRing,
        cigar?.ringGauge != null ? Number(cigar.ringGauge) : undefined
      )

      const submitData = {
        ...data,
        purchaseDate: data.purchaseDate?.toISOString(),
        purchasePrice:
          data.purchasePrice && data.purchasePrice > 0 ? data.purchasePrice : undefined,
        status: data.status as 'available' | 'smoked' | 'traded' | 'gifted' | undefined,
        length: resolvedLength,
        ringGauge: resolvedRingGauge,
      }

      if (!isOnline) {
        const brand = brandsData?.find((b) => b.id === data.brandId)
        const cigarLine = Array.isArray(cigarLinesData)
          ? cigarLinesData.find((l) => l.id === data.cigarLineId)
          : undefined
        const humidor = humidorsData?.find((h) => h.id === data.humidorId)
        const vitola = vitolasData?.find((v) => v.id === data.vitolaId)
        const now = new Date().toISOString()

        if (cigar) {
          const merged: Cigar = {
            ...cigar,
            ...submitData,
            length: submitData.length ?? cigar.length,
            ringGauge: submitData.ringGauge ?? cigar.ringGauge,
            status: (submitData.status ?? cigar.status) as Cigar['status'],
            updatedAt: now,
          }
          await offlineUpdateCigar(dispatch, cigar.id, submitData, merged)
          setFeedback({ type: 'success', message: 'Cigar update saved offline. Will sync when online.' })
        } else {
          const stubLength =
            submitData.length ??
            (vitola && 'length' in vitola && vitola.length != null ? Number(vitola.length) : 0)
          const stubRing =
            submitData.ringGauge ??
            (vitola && 'ringGauge' in vitola && vitola.ringGauge != null ? Number(vitola.ringGauge) : 0)
          const stub: Cigar = {
            id: '',
            brandId: data.brandId,
            cigarLineId: data.cigarLineId,
            humidorId: data.humidorId,
            vitolaId: data.vitolaId,
            quantity: data.quantity,
            length: stubLength,
            ringGauge: stubRing,
            purchasePrice: data.purchasePrice,
            purchaseDate: submitData.purchaseDate,
            purchaseLocation: data.purchaseLocation,
            notes: data.notes,
            status: (submitData.status ?? 'available') as Cigar['status'],
            batchNumber: data.batchNumber,
            wrapperType: data.wrapperType,
            binderType: data.binderType,
            fillerType: data.fillerType,
            strengthLevel: data.strengthLevel,
            imageUrls: [],
            createdAt: now,
            updatedAt: now,
            brand: { id: brand?.id ?? data.brandId, name: brand?.name ?? 'Unknown', country: brand?.country },
            cigarLine: { id: cigarLine?.id ?? data.cigarLineId, name: cigarLine?.name ?? 'Unknown' },
            humidor: { id: humidor?.id ?? data.humidorId, name: humidor?.name ?? 'Unknown' },
            vitola: { id: vitola?.id ?? data.vitolaId, name: vitola?.name ?? 'Unknown' },
          }
          await offlineCreateCigar(dispatch, submitData, stub)
          setFeedback({ type: 'success', message: 'Cigar saved offline. Will sync when online.' })
        }
        onSaved?.()
        handleClose()
        return
      }

      if (cigar) {
        await updateCigar({ id: cigar.id, updates: submitData }).unwrap()
        setFeedback({
          type: 'success',
          message: 'Cigar updated successfully!'
        })
      } else {
        await createCigar(submitData).unwrap()
        setFeedback({
          type: 'success',
          message: 'Cigar added to your collection!'
        })
      }

      onSaved?.()
      handleClose()
    } catch (error: any) {
      console.error('Failed to save cigar:', error)
      
      // Handle specific error types
      if (error?.data?.message) {
        setFeedback({
          type: 'error',
          message: error.data.message
        })
      } else if (error?.status === 400) {
        setFeedback({
          type: 'error',
          message: 'Invalid data provided. Please check all fields and try again.'
        })
      } else if (error?.status === 401) {
        setFeedback({
          type: 'error',
          message: 'You are not authorized. Please log in again.'
        })
      } else if (error?.status === 403) {
        setFeedback({
          type: 'error',
          message: 'Access denied. You do not have permission to modify this humidor.'
        })
      } else if (error?.status === 404) {
        setFeedback({
          type: 'error',
          message: 'Humidor not found. Please select a valid humidor.'
        })
      } else {
        setFeedback({
          type: 'error',
          message: 'Failed to save cigar. Please try again later.'
        })
      }
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const selectedCigarLine = Array.isArray(cigarLinesData) ? cigarLinesData.find(line => line.id === watchedCigarLineId) : undefined

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {cigar ? 'Edit Cigar' : 'Add New Cigar'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(handleFormSubmit)} data-testid="cigar-form">
          <DialogContent>
            <Grid container spacing={3}>
              {/* Brand Selection (searchable) */}
              <Grid item xs={12}>
                <Controller
                  name="brandId"
                  control={control}
                  render={({ field }) => {
                    const brands = Array.isArray(brandsData) ? brandsData : []
                    const selectedBrand = brands.find((brand) => brand.id === field.value) || null

                    return (
                      <Autocomplete
                        options={brands}
                        value={selectedBrand}
                        getOptionLabel={(option) => {
                          if (!option) return ''
                          return option.country
                            ? `${option.name} (${option.country})`
                            : option.name
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(_event, newValue) => {
                          field.onChange(newValue ? newValue.id : '')
                        }}
                        disabled={loading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Brand"
                            error={!!errors.brandId}
                            helperText={errors.brandId?.message}
                            inputProps={{
                              ...params.inputProps,
                              'data-testid': 'brand-select'
                            }}
                          />
                        )}
                      />
                    )
                  }}
                />
              </Grid>

              {/* Cigar Line Selection */}
              <Grid item xs={12}>
                <Controller
                  name="cigarLineId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.cigarLineId}>
                      <InputLabel>Cigar Line</InputLabel>
                      <Select
                        {...field}
                        value={field.value || ''}
                        label="Cigar Line"
                        disabled={loading || !watchedBrandId}
                        data-testid="cigar-line-select"
                      >
                        <MenuItem value="">
                          <em>Select a cigar line</em>
                        </MenuItem>
                        {!watchedBrandId ? (
                          <MenuItem disabled>
                            Please select a brand first
                          </MenuItem>
                        ) : Array.isArray(cigarLinesData) && cigarLinesData.length > 0 ? (
                          cigarLinesData.map((line) => (
                            <MenuItem key={line.id} value={line.id}>
                              {line.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            No cigar lines found for this brand
                          </MenuItem>
                        )}
                      </Select>
                      {errors.cigarLineId && (
                        <FormHelperText>{errors.cigarLineId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Selected Cigar Line Info */}
              {selectedCigarLine && (
                <Grid item xs={12}>
                  <Box p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cigar Line Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Brand:</strong> {selectedCigarLine.brand.name}
                      {selectedCigarLine.brand.country && ` (${selectedCigarLine.brand.country})`}
                    </Typography>
                    {selectedCigarLine.description && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Description:</strong> {selectedCigarLine.description}
                      </Typography>
                    )}
                    {selectedCigarLine.strengthLevel && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Strength:</strong> {selectedCigarLine.strengthLevel}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Vitola and Dimensions */}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="vitolaId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.vitolaId}>
                      <InputLabel>Vitola</InputLabel>
                      <Select
                        {...field}
                        label="Vitola"
                        disabled={loading || !watchedCigarLineId}
                        data-testid="vitola-select"
                        value={field.value || ''}
                        onChange={(event) => {
                          userChoseVitolaRef.current = true
                          const nextVitolaId = event.target.value as string
                          field.onChange(event)
                          // Sync dims synchronously (including A→B→A revert).
                          syncDimensionsForVitola(nextVitolaId)
                        }}
                      >
                        <MenuItem value="">
                          <em>{watchedCigarLineId ? 'Select a vitola' : 'Select a cigar line first'}</em>
                        </MenuItem>
                        {Array.isArray(vitolasData) && vitolasData.map((vitola) => (
                          <MenuItem key={vitola.id} value={vitola.id}>
                            {vitola.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.vitolaId && (
                        <FormHelperText>{errors.vitolaId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const raw = event.target.value
                        field.onChange(raw === '' ? '' : Number(raw))
                      }}
                      label="Quantity"
                      type="number"
                      fullWidth
                      error={!!errors.quantity}
                      helperText={errors.quantity?.message}
                      inputProps={{ min: isEdit ? 0 : 1, max: 1000 }}
                      data-testid="quantity-field"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <Controller
                  name="length"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Length (inches)"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.length}
                      helperText={errors.length?.message}
                      inputProps={{ step: 0.1, min: 0.1, max: 12 }}
                      data-testid="length-input"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <Controller
                  name="ringGauge"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Ring Gauge"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.ringGauge}
                      helperText={errors.ringGauge?.message}
                      inputProps={{ min: 20, max: 80 }}
                      data-testid="ring-gauge-input"
                    />
                  )}
                />
              </Grid>

              {/* Humidor Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="humidorId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.humidorId}>
                      <InputLabel>Humidor</InputLabel>
                      <Select
                        {...field}
                        label="Humidor"
                        disabled={loading}
                        data-testid="humidor-select"
                      >
                        {Array.isArray(humidorsData) && humidorsData.map((humidor: any) => (
                          <MenuItem key={humidor.id} value={humidor.id}>
                            {humidor.name} ({humidor._count?.cigars ?? 0}/{humidor.capacity})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.humidorId && (
                        <FormHelperText>{errors.humidorId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        {...field}
                        label="Status"
                        disabled={loading}
                        data-testid="status-select"
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Purchase Information */}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="purchasePrice"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                      label="Purchase Price"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.purchasePrice}
                      helperText={errors.purchasePrice?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      inputProps={{ step: 0.01, min: 0, max: 10000 }}
                      data-testid="purchase-price-input"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="purchaseDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Purchase Date"
                      disabled={loading}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          disabled: loading,
                          error: !!errors.purchaseDate,
                          helperText: errors.purchaseDate?.message
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="purchaseLocation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Purchase Location"
                      fullWidth
                      disabled={loading}
                      error={!!errors.purchaseLocation}
                      helperText={errors.purchaseLocation?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="wrapperType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Wrapper Type"
                      fullWidth
                      disabled={loading}
                      error={!!errors.wrapperType}
                      helperText={errors.wrapperType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="binderType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Binder Type"
                      fullWidth
                      disabled={loading}
                      error={!!errors.binderType}
                      helperText={errors.binderType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="fillerType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Filler Type"
                      fullWidth
                      disabled={loading}
                      error={!!errors.fillerType}
                      helperText={errors.fillerType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="strengthLevel"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Strength Level"
                      fullWidth
                      disabled={loading}
                      error={!!errors.strengthLevel}
                      helperText={errors.strengthLevel?.message}
                    />
                  )}
                />
              </Grid>

              {/* Batch Number */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="batchNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Batch Number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.batchNumber}
                      helperText={errors.batchNumber?.message}
                    />
                  )}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={3}
                      fullWidth
                      disabled={loading}
                      error={!!errors.notes}
                      helperText={errors.notes?.message || 'Optional — your notes about this stick (e.g. for smoking sessions)'}
                      placeholder="Add your own notes"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || loading}
              data-testid="submit-button"
            >
              {loading ? 'Saving...' : cigar ? 'Update Cigar' : 'Add Cigar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Snackbar
        open={!!feedback}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert 
            onClose={() => setFeedback(null)} 
            severity={feedback.type}
            sx={{ width: '100%' }}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </LocalizationProvider>
  )
}

export default CigarForm
